import { useCallback, useEffect, useState } from 'react';

import { Button } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import noop from 'lodash-es/noop';
import { useIdleTimer } from 'react-idle-timer';
import { useModal } from 'react-modal-hook';

import { useGetCompanySettings } from 'hooks/company';
import { ModalType } from 'shared/lib/analytics/modalTypes';
import { useTrackedShowModal } from 'shared/lib/analytics/useTrackedShowModal';
import { useAuth } from 'shared/lib/auth/useAuth';
import { formatDuration } from 'shared/lib/datetime/formatDate';
import { ResponsiveDialog } from 'shared/ui/ResponsiveDialog';

const DEFAULT_TIMEOUT = 2 ** 31 - 2; // react-idle-timer limitation (signed 32-bit int)
const DEFAULT_PROMPT_BEFORE_IDLE_MS = 2 * 60_000;

export const useSessionExpiredModal = () => {
  const { logout, updateAuth, login, isAuthenticated } = useAuth();

  const { data: company } = useGetCompanySettings();

  const sessionTimeoutMinutes = company?.session_timeout;

  const isTimeoutEnabled = typeof sessionTimeoutMinutes === 'number' && sessionTimeoutMinutes > 0;

  const timeoutMilliseconds = isTimeoutEnabled
    ? Math.min(sessionTimeoutMinutes * 60_000, DEFAULT_TIMEOUT)
    : DEFAULT_TIMEOUT;

  // the value of promptBeforeIdle should be less than timeout
  const promptBeforeIdleMilliseconds = Math.min(DEFAULT_PROMPT_BEFORE_IDLE_MS, timeoutMilliseconds) - 1;

  const [remainingTime, setRemainingTime] = useState(formatDuration(promptBeforeIdleMilliseconds));
  const [isExpired, setIsExpired] = useState(false);

  const [showModal, hideModal] = useModal(
    ({ in: open = false }: TransitionProps) => (
      <ResponsiveDialog
        actions={
          <>
            {!isExpired && (
              <Button
                variant="outlined"
                onClick={() => {
                  hideModal();
                  void logout();
                }}
              >
                Log out
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => {
                if (isExpired) {
                  void login();

                  return;
                }

                activate();
                hideModal();
                void updateAuth();
              }}
            >
              {isExpired ? 'Log in' : 'Stay connected'}
            </Button>
          </>
        }
        open={open}
        subtitle={isExpired ? 'Your session has timed out. Log in again.' : `You will be logged out ${remainingTime}.`}
        title={isExpired ? 'Session expired' : 'Session about to expire'}
        // A timed-out session is a forced choice: no close control, and a no-op `onClose` leaves the
        // backdrop and Escape with nothing to do on either shell
        withCloseIcon={false}
        onClose={noop}
      />
    ),
    [remainingTime, isExpired],
  );

  const showModalWithAnalytics = useTrackedShowModal(showModal, ModalType.SESSION_EXPIRED);

  const handleSessionExpired = useCallback(() => {
    setIsExpired(true);
    showModalWithAnalytics();
  }, [showModalWithAnalytics]);

  const { getRemainingTime, activate } = useIdleTimer({
    timeout: timeoutMilliseconds,
    promptBeforeIdle: promptBeforeIdleMilliseconds,
    startOnMount: isAuthenticated,
    onActive() {
      void updateAuth();
    },
    onPrompt() {
      showModalWithAnalytics();
    },
    onIdle() {
      setIsExpired(true);
    },
  });

  useEffect(() => {
    if (!isTimeoutEnabled) {
      return;
    }

    const interval = setInterval(() => {
      setRemainingTime(formatDuration(getRemainingTime()));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isTimeoutEnabled]);

  return { handleSessionExpired };
};
